import {
  Paper,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { formatCurrencyAmount } from "../../utils/formatters";
import { useRetainedAmounts } from "../../hooks/useRetainedAmounts";
import { DocumentDTO } from "../../types/DocumentDTO";
import { useState } from "react";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  document: DocumentDTO;
  companyId: string;
};

export default function DocumentRetainedAmountsTable({
  document,
  companyId,
}: Props) {
    const { t } = useAppTranslation("common");

    const {
        rows,
        loading,
        save,
        setRows,
        addRow,
        removeRow,
        isValid,
        saving,
    } = useRetainedAmounts(document.id, companyId);


  const [isEditing, setIsEditing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const handleSave = async () => {
    await save();
    setIsEditing(false);
    setSnackbarOpen(true);
  };

  const totalRetainedAmount = rows.reduce(
    (sum, r) => sum + r.retainedAmount,
    0
  );

  const exceedsGross = totalRetainedAmount > document.amounts.gross;

  if (loading) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        {isEditing && (
          <Typography
            variant="caption"
            color="primary"
            sx={{ mb: 1, display: "block" }}
          >
            {t("Tryb edycji kwot zatrzymanych")}
          </Typography>
        )}

        <Typography variant="subtitle1">
          {t("Kwoty zatrzymane")}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2">{t("Pobieranie...")}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
  }}
>
  <Typography variant="subtitle1">
    {t("Kwoty zatrzymane")}
  </Typography>

  <Box sx={{ display: "flex", gap: 1 }}>
    {!isEditing ? (
      <Button
        size="small"
        variant="outlined"
        onClick={() => setIsEditing(true)}
      >
        {t("Edytuj")}
      </Button>
    ) : (
      <>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setIsEditing(false)}
        >
          {t("Anuluj")}
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={addRow}
        >
          {t("Dodaj pozycję")}
        </Button>

        <Button
          size="small"
          variant="contained"
          disabled={!isValid || saving || exceedsGross}
          onClick={handleSave}
        >
          {t("Zapisz")}
        </Button>
      </>
    )}
  </Box>
</Box>
{exceedsGross && (
  <Box
    sx={{
      mb: 2,
      p: 1.5,
      borderRadius: 1,
      backgroundColor: "error.light",
      color: "error.contrastText",
    }}
  >
    <Typography variant="body2">
      {t("Suma kwot zatrzymanych przekracza wartość brutto faktury.")}
    </Typography>
    <Typography variant="caption">
      {t("Maksymalna dopuszczalna wartość")}:{" "}
      {formatCurrencyAmount(document.amounts.gross)}{" "}
      {document.amounts.currency}
    </Typography>
  </Box>
)}

<Divider sx={{ mb: 2 }} />

{rows.length === 0 ? (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ mb: 2 }}
  >
    {t("Brak kwot zatrzymanych. Dodaj pierwszą pozycję.")}
  </Typography>
) : (
  <Table size="small">
    <TableHead sx={{ backgroundColor: "grey.100" }}>
      <TableRow>
        <TableCell>{t("Data zatrzymania")}</TableCell>
        <TableCell align="right">{t("Kwota")}</TableCell>
        <TableCell align="center">{t("Akcje")}</TableCell>
      </TableRow>
    </TableHead>

    <TableBody>
      {rows.map((row) => (
        <TableRow
          key={row.id}
          sx={{
            "&:nth-of-type(odd)": {
              backgroundColor: "grey.50",
            },
          }}
        >
          <TableCell>
                {isEditing ? (
                <TextField
                    type="date"
                    size="small"
                    value={row.retainingDate}
                    onChange={(e) =>
                    setRows((prev) =>
                        prev.map((r) =>
                        r.id === row.id
                            ? { ...r, retainingDate: e.target.value }
                            : r
                        )
                    )
                    }
                />
                ) : (
                row.retainingDate
                )}
            </TableCell>

            <TableCell align="right">
                {isEditing ? (
                <TextField
                    size="small"
                    type="number"
                    onFocus={(e) => {
                      e.target.select();
                    }}
                    value={row.retainedAmount}
                    onChange={(e) =>
                    setRows((prev) =>
                        prev.map((r) =>
                        r.id === row.id
                            ? { ...r, retainedAmount: Number(e.target.value) }
                            : r
                        )
                    )
                    }
                />
                ) : (
                `${formatCurrencyAmount(row.retainedAmount)} ${document.amounts.currency}`
                )}
            </TableCell>

            <TableCell align="center">
                {isEditing && (
                <Tooltip title={t("Usuń pozycję")}>
                    <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeRow(row.id)}
                    >
                    <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                )}
            </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)}
<Box
  sx={{
    mt: 2,
    p: 1,
    backgroundColor: "grey.50",
    borderRadius: 1,
  }}
>
  <Typography variant="caption" color="text.secondary">
    {t("Suma kwot zatrzymanych")}:{" "}
    <strong>
      {formatCurrencyAmount(totalRetainedAmount)}{" "}
      {document.amounts.currency}
    </strong>
    <br />
    {t("Wartość brutto faktury")}:{" "}
    {formatCurrencyAmount(document.amounts.gross)}{" "}
    {document.amounts.currency}
  </Typography>
</Box>

<Snackbar
  open={snackbarOpen}
  autoHideDuration={3000}
  onClose={() => setSnackbarOpen(false)}
  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
>
  <Alert severity="success">{t("Kwoty zatrzymane zapisane")}</Alert>
</Snackbar>

    </Paper>
  );
}
